import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, PencilSimple, Trash, ArrowsClockwise, ShieldCheck, WarningCircle, CheckCircle, GraduationCap } from "@phosphor-icons/react";
import { eur } from "../lib/impresa";

const RICORRENZE = [
  { v: "annuale", l: "Ogni anno" },
  { v: "biennale", l: "Ogni 2 anni" },
  { v: "triennale", l: "Ogni 3 anni" },
  { v: "nessuna", l: "Una volta sola" },
];

const vuoto = {
  tipo: "polizza_rc", descrizione: "", scadenza: "", ricorrenza: "annuale",
  preavviso_giorni: 30, importo: 0, crediti_richiesti: 0, crediti_acquisiti: 0, note: "",
};

const dataIt = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`).toLocaleDateString("it-IT") : "—");

const STATO_CLS = {
  scaduta: "text-red-700 bg-red-50 border-red-200",
  critica: "text-red-700 bg-red-50 border-red-200",
  in_scadenza: "text-amber-700 bg-amber-50 border-amber-200",
  ok: "text-emerald-700 bg-emerald-50 border-emerald-200",
  senza_data: "text-slate-600 bg-slate-100 border-slate-200",
};

const statoLabel = (s) => {
  if (s.stato === "senza_data") return "senza data";
  if (s.giorni_rimanenti < 0) return `scaduta da ${Math.abs(s.giorni_rimanenti)}gg`;
  if (s.giorni_rimanenti === 0) return "scade oggi";
  return `tra ${s.giorni_rimanenti} giorni`;
};

export default function ScadenzeProfessionali() {
  const [items, setItems] = useState([]);
  const [tipi, setTipi] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(vuoto);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/scadenze-professionali");
      setItems(data.items || []);
      setTipi(data.tipi || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const apri = (s = null) => {
    setEditing(s);
    setForm(s ? { ...vuoto, ...s, scadenza: (s.scadenza || "").slice(0, 10) } : vuoto);
    setOpen(true);
  };

  const salva = async (e) => {
    e.preventDefault();
    if (!form.scadenza) { toast.error("Indica la data di scadenza"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        preavviso_giorni: Number(form.preavviso_giorni) || 30,
        importo: Number(form.importo) || 0,
        crediti_richiesti: Number(form.crediti_richiesti) || 0,
        crediti_acquisiti: Number(form.crediti_acquisiti) || 0,
      };
      if (editing) await api.put(`/scadenze-professionali/${editing.id}`, payload);
      else await api.post("/scadenze-professionali", payload);
      toast.success(editing ? "Scadenza aggiornata" : "Scadenza aggiunta");
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const rinnova = async (s) => {
    try {
      const { data } = await api.post(`/scadenze-professionali/${s.id}/rinnova`);
      toast.success(`Rinnovata: nuova scadenza ${dataIt(data.scadenza)}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore rinnovo");
    }
  };

  const elimina = async (s) => {
    if (!window.confirm(`Eliminare "${s.descrizione || s.tipo_label}"?`)) return;
    try {
      await api.delete(`/scadenze-professionali/${s.id}`);
      toast.success("Scadenza eliminata");
      await load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const predefinite = async () => {
    try {
      const { data } = await api.post("/scadenze-professionali/predefinite");
      toast.success(data.creati ? `Aggiunte ${data.creati} scadenze tipiche: controlla le date` : "Le hai già tutte");
      await load();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="p-4 md:p-8 w-full max-w-[1400px] mx-auto" data-testid="scadenze-professionali-page">
      <PageHeader
        eyebrow="Adempimenti del tecnico"
        title="Le mie scadenze"
        description="Polizza RC, quota Albo, PEC, firma digitale e crediti formativi. Ti avviso in dashboard con il preavviso che scegli."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={predefinite} data-testid="scadenze-predefinite-btn">
              <ShieldCheck size={16} /> Aggiungi le tipiche
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => apri(null)} data-testid="nuova-scadenza-btn"><Plus size={16} weight="bold" /> Nuova scadenza</Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editing ? "Modifica scadenza" : "Nuova scadenza"}</DialogTitle></DialogHeader>
                <form onSubmit={salva} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Tipo</Label>
                      <select className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                        value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                        data-testid="scadenza-tipo-select">
                        {tipi.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Scadenza</Label>
                      <Input type="date" value={form.scadenza}
                        onChange={(e) => setForm({ ...form, scadenza: e.target.value })} data-testid="scadenza-data-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Descrizione</Label>
                    <Input value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                      placeholder="es. Polizza RC — Generali n. 123456" data-testid="scadenza-descrizione-input" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>Si rinnova</Label>
                      <select className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                        value={form.ricorrenza} onChange={(e) => setForm({ ...form, ricorrenza: e.target.value })}
                        data-testid="scadenza-ricorrenza-select">
                        {RICORRENZE.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Avvisami (giorni prima)</Label>
                      <Input type="number" min="1" value={form.preavviso_giorni}
                        onChange={(e) => setForm({ ...form, preavviso_giorni: e.target.value })} data-testid="scadenza-preavviso-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Importo (€)</Label>
                      <Input type="number" step="0.01" value={form.importo}
                        onChange={(e) => setForm({ ...form, importo: e.target.value })} data-testid="scadenza-importo-input" />
                    </div>
                  </div>
                  {form.tipo === "crediti_formativi" && (
                    <div className="grid grid-cols-2 gap-3 p-3 rounded-md bg-blue-50 border border-blue-200">
                      <div className="space-y-2">
                        <Label>Crediti richiesti</Label>
                        <Input type="number" value={form.crediti_richiesti}
                          onChange={(e) => setForm({ ...form, crediti_richiesti: e.target.value })} data-testid="scadenza-cfp-richiesti" />
                      </div>
                      <div className="space-y-2">
                        <Label>Crediti acquisiti</Label>
                        <Input type="number" value={form.crediti_acquisiti}
                          onChange={(e) => setForm({ ...form, crediti_acquisiti: e.target.value })} data-testid="scadenza-cfp-acquisiti" />
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Note</Label>
                    <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                      data-testid="scadenza-note-input" />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                    <Button type="submit" disabled={saving} data-testid="scadenza-salva-btn">{saving ? "Salvataggio…" : "Salva"}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {items.length === 0 ? (
        <Card className="p-10 text-center border-slate-200 shadow-none" data-testid="scadenze-vuoto">
          <ShieldCheck size={28} className="mx-auto text-slate-400 mb-3" />
          <div className="font-heading text-lg font-semibold">Nessuna scadenza registrata</div>
          <p className="text-sm text-slate-600 mt-1 mb-4">
            Parti dalle quattro tipiche (polizza RC, quota Albo, PEC, crediti formativi) e correggi le date.
          </p>
          <Button onClick={predefinite} data-testid="scadenze-predefinite-vuoto-btn">
            <ShieldCheck size={16} /> Aggiungi le tipiche
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((s) => {
            const cfp = s.tipo === "crediti_formativi";
            const pct = cfp && s.crediti_richiesti
              ? Math.min(100, Math.round(((s.crediti_acquisiti || 0) / s.crediti_richiesti) * 100)) : null;
            return (
              <Card key={s.id} className="p-5 border-slate-200 shadow-none" data-testid={`scadenza-card-${s.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center">
                    {cfp ? <GraduationCap size={18} className="text-slate-700" />
                      : s.stato === "ok" ? <CheckCircle size={18} className="text-emerald-600" />
                      : <WarningCircle size={18} className="text-amber-600" />}
                  </div>
                  <div className="flex items-center gap-1">
                    {s.ricorrenza !== "nessuna" && (
                      <Button variant="ghost" size="sm" onClick={() => rinnova(s)} title="Rinnova: sposta la data al periodo successivo" data-testid={`scadenza-rinnova-${s.id}`}>
                        <ArrowsClockwise size={14} />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => apri(s)} data-testid={`scadenza-edit-${s.id}`}><PencilSimple size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => elimina(s)} data-testid={`scadenza-del-${s.id}`}><Trash size={14} className="text-red-600" /></Button>
                  </div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{s.tipo_label}</div>
                <div className="font-heading font-semibold text-slate-900 truncate">{s.descrizione || s.tipo_label}</div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-mono tabular-nums text-slate-700">{dataIt(s.scadenza)}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${STATO_CLS[s.stato]}`}>
                    {statoLabel(s)}
                  </span>
                </div>
                {cfp && s.crediti_richiesti > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-[11px] font-mono text-slate-600 mb-1">
                      <span>CFP {s.crediti_acquisiti || 0}/{s.crediti_richiesti}</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${pct >= 100 ? "bg-emerald-600" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-500 space-y-0.5">
                  {s.importo > 0 && <div>Importo: {eur(s.importo)}</div>}
                  <div>Avviso {s.preavviso_giorni || 30} giorni prima · {(RICORRENZE.find((r) => r.v === s.ricorrenza) || {}).l}</div>
                  {s.note && <div className="whitespace-pre-wrap">{s.note}</div>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
